import React, { useEffect, useState } from "react";
import { Alert, Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import toastr from "toastr";

import Modal from "../Modal";
import ApiService from "../../data/services/ApiService";
import { MonedaValores } from "../../data/catalogs/CatalogosSAT";
import {
  CATEGORIAS_EGRESO,
  buildEgresoFormData,
  getInitialEgresoForm,
  parseEgresoXmlPreview
} from "../../helpers/egresoFormHelper";

const findOption = (options, value, fallbackValue) =>
  options.find(option => option.value === value) ||
  options.find(option => option.value === fallbackValue) ||
  options[0];

const EgresoModal = ({
  isOpen,
  mode = "create",
  egreso,
  onClose,
  onSuccess
}) => {
  const [form, setForm] = useState(getInitialEgresoForm(egreso));
  const [xmlFile, setXmlFile] = useState(null);
  const [xmlPreview, setXmlPreview] = useState(null);
  const [autoDetected, setAutoDetected] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(getInitialEgresoForm(egreso));
      setXmlFile(null);
      setXmlPreview(null);
      setAutoDetected(false);
      setError("");
    }
  }, [isOpen, egreso]);

  const updateField = (field, value) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  const updateMoneda = option => {
    const value = option?.value || "MXN";
    setForm(current => ({
      ...current,
      moneda: value,
      tipoCambio: value === "MXN" || value === "XXX" ? 1 : current.tipoCambio
    }));
  };

  const handleFileChange = async file => {
    setError("");
    setXmlFile(file || null);

    if (!file) {
      setXmlPreview(null);
      return;
    }

    try {
      const preview = await parseEgresoXmlPreview(file);
      setXmlPreview(preview);
      setAutoDetected(true);
      setForm(current => ({
        ...current,
        ...preview
      }));
    } catch (err) {
      setError(err.message || "No pude leer el XML.");
      setXmlPreview(null);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError("");

    try {
      setSaving(true);
      const payload = buildEgresoFormData({ form, xmlFile });
      if (mode === "edit") {
        await ApiService.updateEgreso(egreso._id, payload);
      } else {
        await ApiService.createEgreso(payload);
      }

      toastr.success(
        mode === "edit" ? "Egreso actualizado." : "Egreso creado."
      );
      onSuccess();
    } catch (err) {
      const message =
        err.response?.data?.message || "No pude guardar el egreso.";
      setError(message);
      toastr.error(message);
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <form id="egreso-form" onSubmit={handleSubmit}>
      {error && <Alert color="danger">{error}</Alert>}

      {mode === "create" && (
        <FormGroup>
          <Label>XML del CFDI recibido</Label>
          <Input
            type="file"
            accept=".xml"
            onChange={event => handleFileChange(event.target.files?.[0])}
          />
          {xmlPreview && (
            <Alert color="info" className="mt-3">
              <strong>{xmlPreview.emisorNombre || "Emisor sin nombre"}</strong>
              <br />
              RFC: {xmlPreview.emisorRfc || "N/D"} · Total: $
              {xmlPreview.total || "0"} · Fecha:{" "}
              {xmlPreview.fechaGasto || "N/D"} · Uso CFDI:{" "}
              {xmlPreview.usoCFDI || "N/D"}
            </Alert>
          )}
        </FormGroup>
      )}

      <Row>
        <Col md={4} xs={12}>
          <FormGroup>
            <Label>Fecha del gasto</Label>
            <Input
              type="date"
              value={form.fechaGasto}
              onChange={event => updateField("fechaGasto", event.target.value)}
              required
            />
          </FormGroup>
        </Col>
        <Col md={4} xs={12}>
          <FormGroup>
            <Label>Emisor RFC</Label>
            <Input
              value={form.emisorRfc}
              onChange={event => updateField("emisorRfc", event.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md={4} xs={12}>
          <FormGroup>
            <Label>Emisor Nombre</Label>
            <Input
              value={form.emisorNombre}
              onChange={event =>
                updateField("emisorNombre", event.target.value)
              }
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={4} xs={12}>
          <FormGroup>
            <Label>Total</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.total}
              onChange={event => updateField("total", event.target.value)}
              required
            />
          </FormGroup>
        </Col>
        <Col md={4} xs={12}>
          <FormGroup>
            <Label>Moneda</Label>
            <Select
              value={findOption(MonedaValores, form.moneda, "MXN")}
              onChange={updateMoneda}
              options={MonedaValores}
              classNamePrefix="select2-selection"
            />
          </FormGroup>
        </Col>
        {form.moneda !== "MXN" && form.moneda !== "XXX" && (
          <Col md={4} xs={12}>
            <FormGroup>
              <Label>Tipo de cambio</Label>
              <Input
                type="number"
                min="0"
                step="0.0001"
                value={form.tipoCambio}
                onChange={event =>
                  updateField("tipoCambio", event.target.value)
                }
              />
            </FormGroup>
          </Col>
        )}
      </Row>

      <Row>
        <Col md={6} xs={12}>
          <FormGroup>
            <Label>Categoría</Label>
            <Select
              value={findOption(
                CATEGORIAS_EGRESO,
                form.categoria,
                "otros_deducibles"
              )}
              onChange={option =>
                updateField("categoria", option?.value || "otros_deducibles")
              }
              options={CATEGORIAS_EGRESO}
              classNamePrefix="select2-selection"
            />
          </FormGroup>
        </Col>
        <Col md={6} xs={12}>
          <FormGroup style={{ marginTop: 30 }}>
            <Label style={{ display: "block" }}>Deducibilidad</Label>
            <label
              style={{
                alignItems: "center",
                display: "inline-flex",
                gap: 8,
                marginBottom: 0
              }}
            >
              <input
                type="checkbox"
                checked={form.deducible === true}
                onChange={event =>
                  updateField("deducible", event.target.checked)
                }
              />
              Deducible
            </label>
            {autoDetected && (
              <div style={{ fontSize: 12, color: "#74788d", marginTop: 4 }}>
                {form.deducible ? "Deducible" : "No deducible"} detectado por
                Uso CFDI {xmlPreview?.usoCFDI || "N/D"}; puedes cambiarlo
              </div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label>Descripción / Nota</Label>
        <Input
          type="textarea"
          value={form.descripcion}
          onChange={event => updateField("descripcion", event.target.value)}
          rows={3}
        />
      </FormGroup>
    </form>
  );

  const footer = (
    <>
      <Button type="button" color="secondary" onClick={onClose}>
        Cancelar
      </Button>
      <Button
        type="submit"
        color="primary"
        form="egreso-form"
        disabled={saving}
      >
        {saving ? "Guardando..." : "Guardar egreso"}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      title={mode === "edit" ? "Editar egreso" : "Nuevo egreso"}
      body={body}
      footer={footer}
      contentLabel={mode === "edit" ? "Editar egreso" : "Nuevo egreso"}
      styles={{
        content: {
          width: "min(760px, 92vw)",
          maxHeight: "90vh"
        }
      }}
    />
  );
};

export default EgresoModal;
